/// Copyright (C) Microsoft Corporation. All rights reserved.

import { IPublicApiProxy } from '@mecontrol/public-api';
import { createProxy } from './createProxy';
import { logTelemetryEvent } from './logger';

let _proxy = createProxy<IPublicApiProxy>();

export type SetController = typeof _proxy.setComponent;

// controllerSetter will default to V2 public proxy setter, but the method below
// allow us to override what controllerSetter is for backCompat purposes
let controllerSetter: SetController = _proxy.setComponent;

export const getControllerSetter = () => controllerSetter;
export const setControllerSetter = (setter: SetController) => controllerSetter = setter;

// See the definition of IPublicApiProxy for documentation on each of these methods and their call signatures
export const publicProxy: IPublicApiProxy = {
    setActiveAccount: handleCall('setActiveAccount'),
    setCommands: handleCall('setCommands'),
    setAccountPicture: handleCall('setAccountPicture'),
    setProfilePicture: handleCall('setProfilePicture'),
    refreshProfilePicture: handleCall('refreshProfilePicture'),
    refreshAccountPicture: handleCall('refreshAccountPicture'),
    setTheme: handleCall('setTheme'),
    setHeaderTheme: handleCall('setHeaderTheme'),
    setDisplayMode: handleCall('setDisplayMode'),
    loadPresenceConfig: handleCall('loadPresenceConfig'),
    setPresence: handleCall('setPresence'),
    addEventListener: handleCall('addEventListener'),
    removeEventListener: handleCall('removeEventListener')
};

/**
 * Wrapper around the Component Proxy handleComponentCall method to log PartnerApiCall events
 * @param method The event being called
 * @returns A function that both logs a telemetry event and uses the proxy to call the supplied method
 */

function handleCall(method: keyof IPublicApiProxy): (...params: any[]) => void {
    let func = _proxy.handleCall(method);

    return (...params: any[]) => {
        logTelemetryEvent({
            eventType: 'PartnerApiCall',
            method,
            params: [...params]
        });

        func(...params);
    };
}
